/**
 * @file useNotifications.js
 * @description Hook personalizado para gestión de notificaciones toast
 * @module hooks/useNotifications
 * 
 * Características principales:
 * - Gestión de estado de notificaciones activas
 * - Prevención de notificaciones duplicadas (ventana de 1 segundo)
 * - Auto-dismiss con timeouts configurables
 * - Limpieza automática de timeouts al desmontar
 * - Tracking de última notificación para detección de duplicados
 * - Logs de debugging para tracking
 * 
 * Sistema de prevención de duplicados:
 * - Genera key única: `${type}-${title}-${message}`
 * - Compara con última notificación en ventana de 1 segundo
 * - Si coincide: ignora y retorna ID de la existente
 * - Si no coincide: crea nueva notificación
 * 
 * Gestión de timeouts:
 * - Cada notificación tiene su propio timeout para auto-dismiss
 * - Timeouts se limpian automáticamente al remover manualmente
 * - Todos los timeouts se limpian al desmontar o limpiar todas
 * 
 * Uso típico:
 * ```javascript
 * const { notifications, addNotification, removeNotification } = useNotifications();
 * 
 * // Agregar notificación de éxito
 * addNotification({
 *   type: 'success',
 *   title: 'Éxito',
 *   message: 'Operación completada',
 *   duration: 5000 // Auto-dismiss después de 5 segundos
 * });
 * 
 * // Notificación sin auto-dismiss
 * addNotification({
 *   type: 'error',
 *   message: 'Error crítico',
 *   duration: 0 // Permanece hasta cerrar manualmente
 * });
 * 
 * // Renderizar en UI
 * <NotificationContainer
 *   notifications={notifications}
 *   onRemove={removeNotification}
 * />
 * ```
 */

import { useState, useRef } from 'react';

/**
 * Hook useNotifications - Gestión de notificaciones toast
 * 
 * Proporciona funcionalidad completa de sistema de notificaciones:
 * - Agregar notificaciones con validación de duplicados
 * - Remover notificaciones manualmente
 * - Auto-dismiss configurable por notificación
 * - Limpiar todas las notificaciones de golpe
 * 
 * El hook mantiene:
 * - Estado de notificaciones activas (array)
 * - Referencia de última notificación (para duplicados)
 * - Map de timeouts activos (para limpieza)
 * 
 * @hook
 * @returns {Object} Objeto con estado y métodos de notificaciones
 * @returns {Array<Object>} return.notifications - Array de notificaciones activas
 * @returns {Function} return.addNotification - Agrega nueva notificación
 * @returns {Function} return.removeNotification - Remueve notificación por ID
 * @returns {Function} return.clearAllNotifications - Limpia todas las notificaciones
 */
export const useNotifications = () => {
  /**
   * Estado: Array de notificaciones activas
   * 
   * Cada notificación tiene:
   * - id: Timestamp único + random
   * - type: 'success' | 'error' | 'warning' | 'info'
   * - title: Título opcional
   * - message: Mensaje principal
   * - duration: Tiempo en ms antes de auto-dismiss (0 = permanente)
   */
  const [notifications, setNotifications] = useState([]);
  
  /**
   * Ref: Última notificación agregada (para prevenir duplicados)
   * 
   * Estructura:
   * - id: ID de la notificación
   * - key: String único basado en contenido (type-title-message)
   * - timestamp: Momento en que se creó (Date.now())
   * 
   * Se usa para detectar si una notificación idéntica fue creada
   * en los últimos 1000ms (1 segundo).
   */
  const lastNotificationRef = useRef(null);
  
  /**
   * Ref: Map de timeouts activos
   * 
   * Estructura: Map<notificationId, timeoutId>
   * 
   * Permite:
   * - Limpiar timeout al remover notificación manualmente
   * - Limpiar todos los timeouts al desmontar o limpiar todas
   * - Evitar memory leaks de timeouts huérfanos
   */
  const timeoutRefs = useRef(new Map());

  /**
   * Agrega una nueva notificación al sistema
   * 
   * Flujo:
   * 1. Genera key única basada en contenido
   * 2. Verifica si es duplicado de última notificación (< 1 segundo)
   * 3. Si es duplicado: ignora y retorna ID existente
   * 4. Si no es duplicado:
   *    a. Genera ID único (timestamp + random)
   *    b. Crea objeto de notificación con defaults
   *    c. Guarda referencia para detección de duplicados
   *    d. Agrega al estado
   *    e. Configura auto-dismiss si duration > 0
   * 
   * Prevención de duplicados:
   * - Compara tipo, título y mensaje
   * - Ventana de 1 segundo
   * - Útil cuando múltiples eventos disparan la misma notificación
   * 
   * @param {Object} notification - Configuración de la notificación
   * @param {('success'|'error'|'warning'|'info')} [notification.type='info'] - Tipo visual
   * @param {string} [notification.title=''] - Título opcional
   * @param {string} [notification.message=''] - Mensaje principal
   * @param {number} [notification.duration=5000] - Duración en ms (0 = permanente)
   * @returns {number} ID de la notificación (nueva o existente si es duplicada)
   */
  const addNotification = (notification) => {
    // Paso 1: Generar key única para detección de duplicados
    const notificationKey = `${notification.type}-${notification.title}-${notification.message}`;
    const now = Date.now();
    
    // Paso 2: Verificar si es duplicado (misma key en < 1 segundo)
    if (lastNotificationRef.current?.key === notificationKey && 
        (now - lastNotificationRef.current.timestamp) < 1000) {
      console.log('Notificación duplicada prevenida:', notificationKey);
      return lastNotificationRef.current.id;
    }

    // Paso 3: Crear nueva notificación
    const id = now + Math.random(); // ID único
    const newNotification = {
      id,
      type: 'info',      // Default: info
      title: '',         // Default: sin título
      message: '',       // Default: sin mensaje
      duration: 5000,    // Default: 5 segundos
      ...notification    // Sobrescribir con valores proporcionados
    };

    // Paso 4: Guardar referencia para prevenir futuros duplicados
    lastNotificationRef.current = {
      id,
      key: notificationKey,
      timestamp: now
    };
    
    // Paso 5: Agregar al estado
    setNotifications(prev => [...prev, newNotification]);

    // Paso 6: Configurar auto-dismiss si tiene duración
    if (newNotification.duration > 0) {
      const timeoutId = setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
      
      // Guardar referencia del timeout para limpieza posterior
      timeoutRefs.current.set(id, timeoutId);
    }

    return id;
  };

  /**
   * Remueve una notificación del sistema
   * 
   * Flujo:
   * 1. Filtra la notificación del estado por ID
   * 2. Busca y limpia el timeout asociado (si existe)
   * 3. Elimina la entrada del Map de timeouts
   * 
   * Casos de uso:
   * - Click manual en botón X de la notificación
   * - Auto-dismiss cuando expira el timeout
   * - Limpieza programática desde componente padre
   * 
   * @param {number} id - ID único de la notificación a remover
   */
  const removeNotification = (id) => {
    console.log('Removiendo notificación:', id);
    
    // Paso 1: Remover del estado
    setNotifications(prev => prev.filter(notif => notif.id !== id));
    
    // Paso 2: Limpiar timeout asociado (si existe)
    const timeoutId = timeoutRefs.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutRefs.current.delete(id);
    }
  };

  /**
   * Limpia todas las notificaciones del sistema
   * 
   * Flujo:
   * 1. Vacía el array de notificaciones
   * 2. Limpia todos los timeouts activos
   * 3. Vacía el Map de timeouts
   * 4. Resetea la referencia de última notificación
   * 
   * Casos de uso:
   * - Limpiar todas las notificaciones al cambiar de página
   * - Resetear el sistema de notificaciones
   * - Limpieza al desmontar un componente padre
   * - Botón "Limpiar todas" en UI (si existe)
   * 
   * IMPORTANTE: Previene memory leaks limpiando todos los timeouts
   * antes de vaciar el estado.
   */
  const clearAllNotifications = () => {
    // Paso 1: Vaciar estado de notificaciones
    setNotifications([]);
    
    // Paso 2: Limpiar todos los timeouts activos
    timeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
    
    // Paso 3: Vaciar Map de timeouts
    timeoutRefs.current.clear();
    
    // Paso 4: Resetear referencia de última notificación
    lastNotificationRef.current = null;
  };

  /**
   * Retorna API pública del hook
   * 
   * Métodos expuestos:
   * - notifications: Array reactivo de notificaciones activas
   * - addNotification: Agregar nueva notificación
   * - removeNotification: Remover por ID
   * - clearAllNotifications: Limpiar todas
   */
  return {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications
  };
};

/**
 * NOTAS DE IMPLEMENTACIÓN:
 * 
 * 1. Prevención de duplicados:
 *    - Útil para evitar spam cuando múltiples eventos disparan notificaciones
 *    - Ventana de 1 segundo es configurable ajustando la comparación
 *    - Key basada en contenido (tipo + título + mensaje)
 * 
 * 2. Gestión de memoria:
 *    - Todos los timeouts se guardan en Map para limpieza
 *    - Limpieza automática al remover manualmente
 *    - Limpieza masiva al desmontar
 * 
 * 3. IDs únicos:
 *    - Date.now() + Math.random() evita colisiones
 *    - Suficiente para sistema de notificaciones (no crítico)
 *    - Alternativa: usar nanoid o uuid si se requiere mayor robustez
 * 
 * 4. Duración 0:
 *    - duration: 0 significa notificación permanente
 *    - No se crea timeout
 *    - Usuario debe cerrar manualmente
 *    - Útil para errores críticos que requieren atención
 */